(() => {
  const selector = '[data-ln-pillow-skinny-bundle]';

  class PillowSkinnyBundleHero {
    constructor(root) {
      this.root = root;
      this.busy = false;
      this.abortController = new AbortController();
      this.pillowIndex = Number(root.dataset.pillowOptionIndex || 0);
      this.skinnyIndex = Number(root.dataset.skinnyOptionIndex || 1);
      this.variants = this.readVariants();
      this.sortSwatchesByManualOrder();
      this.bind();
      this.sync(false);
      this.observeSticky();
    }

    readVariants() {
      const source = this.root.querySelector('[data-ln-bundle-variants]');
      if (!source) return [];
      try {
        const parsed = JSON.parse(source.textContent || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (_) {
        return [];
      }
    }

    sortSwatchesByManualOrder() {
      ['pillow', 'skinny'].forEach((role) => {
        const swatches = this.swatches(role);
        if (swatches.length < 2) return;
        const parent = swatches[0].parentElement;
        if (!parent || swatches.some((swatch) => swatch.parentElement !== parent)) return;
        swatches
          .sort((first, second) => Number(first.dataset.manualOrder || 1000) - Number(second.dataset.manualOrder || 1000))
          .forEach((swatch) => parent.appendChild(swatch));
      });
    }

    bind() {
      const options = { signal: this.abortController.signal };
      this.root.addEventListener('click', (event) => this.onClick(event), options);
      this.root.addEventListener('submit', (event) => this.onSubmit(event), options);
    }

    swatches(role) {
      return Array.from(this.root.querySelectorAll(`[data-ln-bundle-swatch][data-bundle-role="${role}"]`));
    }

    selected(role) {
      return this.root.querySelector(`[data-ln-bundle-swatch][data-bundle-role="${role}"][aria-pressed="true"]`);
    }

    selectedVariant() {
      const pillow = this.selected('pillow')?.dataset.optionValue;
      const skinny = this.selected('skinny')?.dataset.optionValue;
      if (!pillow || !skinny) return null;
      return this.variants.find((variant) => {
        const options = variant.options || [];
        return options[this.pillowIndex] === pillow && options[this.skinnyIndex] === skinny;
      }) || null;
    }

    money(cents) {
      if (!Number.isFinite(Number(cents))) return '—';
      if (window.Shopify?.formatMoney && this.root.dataset.moneyFormat) {
        const container = document.createElement('span');
        container.innerHTML = window.Shopify.formatMoney(Number(cents), this.root.dataset.moneyFormat);
        return container.textContent;
      }
      const currency = this.root.dataset.currency || this.root.dataset.shopCurrency || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
          minimumFractionDigits: Number(cents) % 100 ? 2 : 0
        }).format(Number(cents) / 100);
      } catch (_) {
        return `${currency} ${(Number(cents) / 100).toFixed(2)}`;
      }
    }

    onClick(event) {
      const swatch = event.target.closest?.('[data-ln-bundle-swatch]');
      if (swatch && this.root.contains(swatch) && !swatch.disabled && !this.busy) {
        this.selectSwatch(swatch);
        return;
      }

      const gallery = event.target.closest?.('[data-ln-bundle-product-thumb], [data-lp-b-gallery-media]');
      if (gallery && this.root.contains(gallery)) {
        this.selectGallery(gallery);
        return;
      }

      const sticky = event.target.closest?.('[data-ln-bundle-sticky-button]');
      if (sticky && this.root.contains(sticky)) {
        event.preventDefault();
        this.addToCart(sticky);
      }
    }

    onSubmit(event) {
      const form = event.target.closest?.('[data-ln-bundle-product-form]');
      if (!form || !this.root.contains(form)) return;
      event.preventDefault();
      this.addToCart(event.submitter || form.querySelector('[data-ln-bundle-main-atc]'));
    }

    selectSwatch(swatch) {
      const role = swatch.dataset.bundleRole;
      this.swatches(role).forEach((button) => {
        const selected = button === swatch;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      this.sync(true);
    }

    sync(returnToProduct) {
      const pillow = this.selected('pillow');
      const skinny = this.selected('skinny');
      const variant = this.selectedVariant();

      this.root.querySelectorAll('[data-ln-bundle-selected-pillow]').forEach((label) => {
        label.textContent = pillow?.dataset.optionValue || '';
      });
      this.root.querySelectorAll('[data-ln-bundle-selected-skinny]').forEach((label) => {
        label.textContent = skinny?.dataset.optionValue || '';
      });
      this.root.querySelectorAll('[data-ln-bundle-media-colors], [data-ln-bundle-sticky-colors]').forEach((label) => {
        label.textContent = [pillow?.dataset.optionValue, skinny?.dataset.optionValue].filter(Boolean).join(' + ');
      });

      this.updateImage(this.root.querySelector('[data-ln-bundle-pillow-image]'), pillow);
      this.updateImage(this.root.querySelector('[data-ln-bundle-skinny-image]'), skinny);
      this.updateImage(this.root.querySelector('[data-ln-bundle-compact-pillow]'), pillow, false);
      this.updateImage(this.root.querySelector('[data-ln-bundle-compact-skinny]'), skinny, false);
      this.updateImage(this.root.querySelector('[data-ln-bundle-featured-pillow]'), pillow, false);
      this.updateImage(this.root.querySelector('[data-ln-bundle-featured-skinny]'), skinny, false);
      this.updateImage(this.root.querySelector('[data-ln-bundle-thumb-pillow]'), pillow, false);
      this.updateImage(this.root.querySelector('[data-ln-bundle-thumb-skinny]'), skinny, false);

      if (pillow?.dataset.backgroundAccent) {
        this.root.style.setProperty('--ln-pillow-editorial-accent', pillow.dataset.backgroundAccent);
      }

      const price = variant ? this.money(variant.price) : '—';
      this.root.querySelectorAll('[data-lp-b-price], [data-ln-bundle-card-price], [data-ln-bundle-sticky-price]').forEach((label) => {
        label.textContent = price;
      });
      this.root.querySelectorAll('[data-ln-bundle-variant-input]').forEach((input) => {
        input.value = variant?.id || '';
      });
      this.root.querySelectorAll('[data-ln-bundle-availability]').forEach((label) => {
        label.textContent = variant?.available ? 'Available' : 'Sold out';
      });

      this.updateButtons(variant);
      if (returnToProduct) this.showBundleMedia();
      if (variant?.id && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({ variantId: variant.id }, '', url);
      }
    }

    updateImage(image, swatch, responsive = true) {
      if (!image || !swatch?.dataset.mediaSrc) return;
      const src = responsive ? swatch.dataset.mediaSrc : (swatch.dataset.previewSrc || swatch.dataset.mediaSrc);
      const srcset = swatch.dataset.mediaSrcset || '';
      if (image.dataset.currentSrc === src && image.dataset.currentSrcset === srcset) return;
      image.dataset.currentSrc = src;
      image.dataset.currentSrcset = srcset;
      if (responsive && srcset) image.srcset = srcset;
      else image.removeAttribute('srcset');
      image.src = src;
      image.alt = swatch.dataset.mediaAlt || swatch.dataset.optionValue || '';
    }

    updateButtons(variant) {
      const available = Boolean(variant?.available);
      const price = variant ? this.money(variant.price) : '—';
      const addLabel = this.root.dataset.addLabel || 'ADD TO CART';
      const stickyLabel = this.root.dataset.stickyAddLabel || addLabel;
      const soldOut = this.root.dataset.soldOutLabel || 'Sold Out';
      const mainText = available ? `${addLabel} — ${price}` : soldOut;
      const stickyText = available ? `${stickyLabel} — ${price}` : soldOut;

      this.root.querySelectorAll('[data-ln-bundle-main-atc]').forEach((button) => {
        button.disabled = !available || this.busy;
        button.textContent = this.busy ? (this.root.dataset.addingLabel || 'Adding…') : mainText;
        button.setAttribute('aria-busy', String(this.busy));
      });
      this.root.querySelectorAll('[data-ln-bundle-sticky-button]').forEach((button) => {
        button.disabled = !available || this.busy;
        button.textContent = this.busy ? (this.root.dataset.addingLabel || 'Adding…') : stickyText;
        button.setAttribute('aria-busy', String(this.busy));
      });
    }

    selectGallery(control) {
      this.root.querySelectorAll('[data-ln-bundle-product-thumb], [data-lp-b-gallery-media]').forEach((button) => {
        const active = button === control;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      if (control.matches('[data-ln-bundle-product-thumb]')) {
        this.showBundleMedia();
      } else {
        this.showGalleryMedia(control);
      }
    }

    showBundleMedia() {
      this.root.classList.add('is-duo-media-active');
      const media = this.root.querySelector('[data-ln-bundle-media]');
      if (media) media.hidden = false;
      const productThumb = this.root.querySelector('[data-ln-bundle-product-thumb]');
      if (productThumb) {
        this.root.querySelectorAll('[data-ln-bundle-product-thumb], [data-lp-b-gallery-media]').forEach((button) => {
          const active = button === productThumb;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
      }
    }

    showGalleryMedia(control) {
      const src = control.dataset.mediaSrc;
      if (!src) return;
      this.root.classList.remove('is-duo-media-active');
      const media = this.root.querySelector('[data-ln-bundle-media]');
      if (media) media.hidden = true;
      const scene = this.root.querySelector('[data-lp-b-scene].is-active') || this.root.querySelector('[data-lp-b-scene]');
      const product = scene?.querySelector('.ln-pillow-b-scene__product');
      const image = product?.querySelector('.ln-pillow-b-scene__image');
      if (!scene || !product || !image) return;
      product.classList.add('ln-pillow-b-scene__product--model');
      scene.style.setProperty('--lpb-rotation', '0deg');
      scene.style.setProperty('--lpb-scale', '1');
      image.src = src;
      if (control.dataset.mediaSrcset) image.srcset = control.dataset.mediaSrcset;
      else image.removeAttribute('srcset');
      image.alt = control.dataset.mediaAlt || '';
    }

    async addToCart(trigger) {
      const variant = this.selectedVariant();
      if (this.busy || !variant?.available) return;
      this.busy = true;
      this.updateButtons(variant);
      this.setStatus('');
      let added = false;
      try {
        const cartUI = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const sections = [...new Set((cartUI?.getSectionsToRender?.() || []).map((section) => section.id))].slice(0, 5);
        const rootUrl = window.Shopify?.routes?.root || '/';
        const response = await fetch(window.routes?.cart_add_url || `${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [{ id: Number(variant.id), quantity: 1 }],
            ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname } : {})
          })
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || this.root.dataset.errorLabel || 'Could not add this bundle.');
        added = true;
        await this.renderCart(data, cartUI, trigger);
        this.setStatus(this.root.dataset.addedLabel || 'Added');
      } catch (error) {
        this.setStatus(error.message || this.root.dataset.errorLabel || 'Could not add this bundle.');
      } finally {
        this.busy = false;
        this.updateButtons(this.selectedVariant());
        if (added) document.dispatchEvent(new CustomEvent('cart:updated'));
      }
    }

    async renderCart(data, cartUI, trigger) {
      if (typeof cartUI?.renderContents === 'function' && data.sections) {
        cartUI.classList.remove('is-empty');
        cartUI.setActiveElement?.(trigger);
        cartUI.renderContents({ ...data, ...(data.items?.[0] || {}), sections: data.sections });
        return;
      }
      const rootUrl = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${rootUrl}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
        bubble.classList.toggle('hidden', cart.item_count === 0);
        const visible = bubble.querySelector('[aria-hidden="true"]');
        const hidden = bubble.querySelector('.visually-hidden');
        if (visible) visible.textContent = cart.item_count;
        if (hidden) hidden.textContent = `${cart.item_count} items`;
      });
    }

    setStatus(message) {
      this.root.querySelectorAll('[data-ln-bundle-cart-status]').forEach((status) => {
        status.textContent = message;
      });
    }

    observeSticky() {
      const sticky = this.root.querySelector('[data-ln-bundle-sticky]');
      if (!sticky || !('IntersectionObserver' in window)) return;
      this.stickyObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        sticky.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.bottom <= 0);
      }, { threshold: 0 });
      this.stickyObserver.observe(this.root);
    }

    destroy() {
      this.stickyObserver?.disconnect();
      this.abortController.abort();
    }
  }

  const controllers = new Map();

  const boot = () => {
    controllers.forEach((controller, root) => {
      if (!root.isConnected) {
        controller.destroy();
        controllers.delete(root);
      }
    });
    document.querySelectorAll(selector).forEach((root) => {
      if (!controllers.has(root)) controllers.set(root, new PillowSkinnyBundleHero(root));
    });
  };

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', () => window.setTimeout(boot, 0));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
